import { useQuery } from "@tanstack/react-query";
import { Bot, ChevronRight, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";
import { formatTokens, formatUsd } from "../../lib/format";

export function AgentsTab({ accountId }: { accountId: string }) {
  const { token } = useAdminAuth();
  const funnels = useQuery({
    queryKey: ["admin", "account", accountId, "funnels"],
    queryFn: () => adminApi.accountFunnels(token!, accountId),
    enabled: Boolean(token),
  });

  if (funnels.isLoading) {
    return (
      <div className="panel p-4 text-sm text-muted-foreground">
        Загружаем агентов...
      </div>
    );
  }

  if (funnels.error) {
    return (
      <div className="panel p-4 text-sm text-danger">
        {funnels.error.message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">AI-агенты</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Каждый агент привязан к одной воронке amoCRM.
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            LLM-токены и стоимость — только для входящей обработки.
            Сгенерированные касания и аналитические отчёты не включены.
          </p>
        </div>
        <Link
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
          to={`/accounts/${accountId}/agents/new`}
        >
          <Plus className="h-4 w-4" />
          Создать агента
        </Link>
      </div>

      {funnels.data?.length === 0 ? (
        <div className="panel p-4 text-sm text-muted-foreground">
          Агентов пока нет.
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {(funnels.data ?? []).map((funnel) => (
            <Link
              key={funnel.id}
              className="panel flex items-start justify-between gap-3 p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
              to={`/funnels/${funnel.id}`}
              aria-label={`Настроить ${funnel.name}`}
            >
              <div className="flex min-w-0 gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                  <Bot className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-medium">{funnel.name}</p>
                    <span
                      className={`rounded-md px-2 py-0.5 text-xs ${
                        funnel.active
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {funnel.active ? "активен" : "выключен"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Pipeline {funnel.amoPipelineId} · каналов{" "}
                    {funnel.channels.length} · диалогов {funnel.dialogsCount}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    LLM входящих: {funnel.model} · {formatTokens(funnel.llmUsage.totalTokens)}{" "}
                    ток. · {formatUsd(funnel.llmUsage.costUsd)}
                  </p>
                </div>
              </div>
              <ChevronRight className="mt-2 h-5 w-5 shrink-0 text-muted-foreground" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
